import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Smartphone, Laptop, Tablet, Monitor, Loader2, CheckCircle, Store, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DEVICE_TYPES = [
  { value: "mobile", label: "Mobile Phone", icon: Smartphone },
  { value: "laptop", label: "Laptop", icon: Laptop },
  { value: "tablet", label: "Tablet", icon: Tablet },
  { value: "desktop", label: "Desktop/PC", icon: Monitor },
  { value: "other", label: "Other", icon: Smartphone },
];

const BRANDS = {
  mobile: ["Apple", "Samsung", "OnePlus", "Xiaomi", "Realme", "Oppo", "Vivo", "Google", "Nothing", "Motorola", "Other"],
  laptop: ["Apple", "Dell", "HP", "Lenovo", "Asus", "Acer", "MSI", "Microsoft", "Samsung", "Other"],
  tablet: ["Apple", "Samsung", "Lenovo", "Microsoft", "Amazon", "Xiaomi", "Other"],
  desktop: ["Dell", "HP", "Lenovo", "Asus", "Acer", "Apple", "Custom Build", "Other"],
  other: ["Other"],
};

// Helper to get subdomain from hostname or URL
const getSubdomain = () => {
  // First check URL params
  const urlParams = new URLSearchParams(window.location.search);
  const subdomainParam = urlParams.get("subdomain");
  if (subdomainParam) return subdomainParam;
  
  // Then check hostname (for production: demo.aftersales.pro)
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  if (parts.length >= 3 && parts[1] === "aftersales") {
    return parts[0];
  }
  
  return null;
};

export default function CustomerIntake() {
  const { subdomain: routeSubdomain } = useParams();
  const [searchParams] = useSearchParams();
  const subdomain = routeSubdomain || searchParams.get("subdomain") || getSubdomain();
  
  const [shopInfo, setShopInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    device_type: "",
    device_brand: "",
    device_model: "",
    imei_serial: "",
    problem_description: "",
    device_password: "",
    accessories: "",
    preferred_contact: "phone",
  });

  useEffect(() => {
    if (subdomain) {
      fetchShopInfo();
    } else {
      setError("No shop specified. Please scan a valid QR code.");
      setLoading(false);
    }
  }, [subdomain]);

  const fetchShopInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/public/shop/${subdomain}/info`);
      setShopInfo(res.data);
    } catch (err) {
      setError("Shop not found. Please check the URL and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    if (name === "device_type") {
      setFormData({ ...formData, [name]: value, device_brand: "" });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.customer_phone.trim() || formData.customer_phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!formData.device_type) {
      toast.error("Please select a device type");
      return;
    }
    if (!formData.device_brand) {
      toast.error("Please select a device brand");
      return;
    }
    if (!formData.problem_description.trim()) {
      toast.error("Please describe the problem");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/shop/${subdomain}/customer-intake`, formData);
      setSubmitted(true);
      toast.success("Details submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Shop Not Found</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-4">
              Your details have been submitted to <strong>{shopInfo?.company_name}</strong>.
            </p>
            <p className="text-sm text-gray-500">
              The shop will contact you shortly to proceed with your repair.
            </p>
            <Button 
              className="mt-6"
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  customer_name: "",
                  customer_phone: "",
                  customer_email: "",
                  device_type: "",
                  device_brand: "",
                  device_model: "",
                  imei_serial: "",
                  problem_description: "",
                  device_password: "",
                  accessories: "",
                  preferred_contact: "phone",
                });
              }}
            >
              Submit Another Device
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Shop Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Store className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{shopInfo?.company_name}</h1>
          <p className="text-gray-600">Device Repair Intake Form</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Enter Your Details</CardTitle>
            <CardDescription>
              Fill in your information and device details. The shop will contact you to proceed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Customer Info */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 border-b pb-2">Your Information</h3>
                
                <div>
                  <Label htmlFor="customer_name">Full Name *</Label>
                  <Input
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer_phone">Phone Number *</Label>
                  <Input
                    id="customer_phone"
                    name="customer_phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer_email">Email (Optional)</Label>
                  <Input
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <Label>Preferred Contact Method</Label>
                  <Select value={formData.preferred_contact} onValueChange={(v) => handleSelectChange("preferred_contact", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone Call</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Device Info */}
              <div className="space-y-4 pt-4">
                <h3 className="font-medium text-gray-900 border-b pb-2">Device Information</h3>
                
                <div>
                  <Label>Device Type *</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {DEVICE_TYPES.slice(0, 3).map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleSelectChange("device_type", type.value)}
                          className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                            formData.device_type === type.value
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <Icon className="w-6 h-6" />
                          <span className="text-xs font-medium">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {DEVICE_TYPES.slice(3).map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleSelectChange("device_type", type.value)}
                          className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                            formData.device_type === type.value
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <Icon className="w-6 h-6" />
                          <span className="text-xs font-medium">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {formData.device_type && (
                  <div>
                    <Label>Brand *</Label>
                    <Select value={formData.device_brand} onValueChange={(v) => handleSelectChange("device_brand", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {(BRANDS[formData.device_type] || BRANDS.other).map((brand) => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="device_model">Model (Optional)</Label>
                  <Input
                    id="device_model"
                    name="device_model"
                    value={formData.device_model}
                    onChange={handleChange}
                    placeholder="e.g., iPhone 15 Pro, Galaxy S24"
                  />
                </div>

                <div>
                  <Label htmlFor="imei_serial">IMEI / Serial Number (Optional)</Label>
                  <Input
                    id="imei_serial"
                    name="imei_serial"
                    value={formData.imei_serial}
                    onChange={handleChange}
                    placeholder="Found in Settings > About Phone"
                  />
                </div>
              </div>

              {/* Problem Info */}
              <div className="space-y-4 pt-4">
                <h3 className="font-medium text-gray-900 border-b pb-2">Problem Details</h3>
                
                <div>
                  <Label htmlFor="problem_description">Describe the Problem *</Label>
                  <Textarea
                    id="problem_description"
                    name="problem_description"
                    value={formData.problem_description}
                    onChange={handleChange}
                    placeholder="e.g., Screen cracked, not charging, water damage, battery drains fast..."
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="device_password">Device Password/PIN (Optional)</Label>
                  <Input
                    id="device_password"
                    name="device_password"
                    value={formData.device_password}
                    onChange={handleChange}
                    placeholder="For diagnosis (kept confidential)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required if the device needs to be unlocked for testing
                  </p>
                </div>

                <div>
                  <Label htmlFor="accessories">Accessories Submitted (Optional)</Label>
                  <Input
                    id="accessories"
                    name="accessories"
                    value={formData.accessories}
                    onChange={handleChange}
                    placeholder="e.g., Charger, case, earphones"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Details"
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                By submitting, you agree to share your details with {shopInfo?.company_name} for repair purposes.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Powered by footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by <a href="https://aftersales.pro" className="text-blue-500 hover:underline">AfterSales.pro</a>
        </p>
      </div>
    </div>
  );
}
